import { Component, OnInit, NgZone, ViewChild, ElementRef } from '@angular/core';
import { FormControl, FormGroup, Validators, FormBuilder } from '@angular/forms';
import { MustMatch } from '../../helpers/must-match.validator';
import { UserService } from 'src/app/services/user.service';
import { catchError, debounceTime, map } from 'rxjs/operators';
import { throwError, Subject } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { getLocalTranslate } from 'src/app/helpers/global-functions';
import { AuthService } from 'src/app/services/auth.service';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.css']
})
export class RegisterComponent implements OnInit {

  @ViewChild('selectSex') selectSex!: ElementRef;

  public showSpinner = false;

  registerForm!: FormGroup;

  subject = new Subject<string>();

  isUserExist = false;
  isCheckingUser = false;
  isShowRepeat = false;
  isShow = false;
  otpSent = false;
  otpVerified = false;
  isSendingOtp = false;
  isVerifyingOtp = false;

  otpCode = '';
  otpEmail = '';

  newAccount = 'Create new Account';
  signedUp = 'Already signed up?';
  login = 'Login';
  nameText = 'Name';
  sexText = 'Sex';
  male = 'Male';
  female = 'Female';
  dobText = 'Date of Birth';
  phoneText = 'Phone';
  emailForLogin = 'Email for Login';
  passwordText = 'Password';
  repeatPasswordText = 'Repeat password';
  signUp = 'Sign Up';

  constructor(
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private service: UserService,
    private zone: NgZone,
    private router: Router,
    private toastr: ToastrService,
    private titleService: Title
  ) { }

  ngOnInit() {
    this.needTranslate();
    this.registerForm = this.formBuilder.group(
      {
        name: new FormControl('', [Validators.required]),
        email: new FormControl('', [Validators.required, Validators.email]),
        phoneNumber: new FormControl('', [Validators.required, Validators.pattern('[- +()0-9]{8,9}')]),
        dob: new FormControl('', Validators.required),
        sex: new FormControl(''),
        password: new FormControl('', [Validators.required, Validators.minLength(6)]),
        repeatPassword: new FormControl('', [Validators.required, Validators.minLength(6)])
      },
      {
        validators: [MustMatch('password', 'repeatPassword')]
      }
    );

    this.phoneNumber?.valueChanges.subscribe(value => {
      if (!value) {
        return;
      }

      if (value[0] === '0') {
        this.phoneNumber.setValidators([Validators.required, Validators.pattern('[- +()0-9]{9,10}')]);
      } else {
        this.phoneNumber.setValidators([Validators.required, Validators.pattern('[- +()0-9]{8,9}')]);
      }

      this.phoneNumber.updateValueAndValidity();
    });

    this.handleEmailTypingObserver();
  }

  needTranslate() {
    this.newAccount = getLocalTranslate('new-account', this.newAccount);
    this.signedUp = getLocalTranslate('signed-up', this.signedUp);
    this.login = getLocalTranslate('login', this.login);
    this.nameText = getLocalTranslate('name', this.nameText);
    this.sexText = getLocalTranslate('sex', this.sexText);
    this.male = getLocalTranslate('male', this.male);
    this.female = getLocalTranslate('female', this.female);
    this.phoneText = getLocalTranslate('phone', this.phoneText);
    this.emailForLogin = getLocalTranslate('email-for-login', this.emailForLogin);
    this.passwordText = getLocalTranslate('password', this.passwordText);
    this.repeatPasswordText = getLocalTranslate('repeat-password', this.repeatPasswordText);
    this.signUp = getLocalTranslate('sign-up', this.signUp);
    this.dobText = getLocalTranslate('dob', this.dobText);
    this.titleService.setTitle(`${getLocalTranslate('sign-up', this.signUp)} | CamEdu`);
  }

  public numbersOnlyValidator(event: any) {
    const pattern = /^[0-9\-]*$/;
    if (!pattern.test(event.target.value)) {
      event.target.value = event.target.value.replace(/[^0-9\-]/g, '');
    }
  }

  handleEmailTypingObserver() {
    this.subject.pipe(
      debounceTime(1000),
      map(searchText => searchText)
    ).subscribe(result => {
      this.isUserExist = false;

      if (this.email?.invalid) {
        return;
      }

      this.isCheckingUser = true;
      this.service.checkExistUser(result).subscribe(status => {
        this.isCheckingUser = false;
        this.isUserExist = status;
      });
    });
  }

  get name() { return this.registerForm.get('name') as FormControl; }
  get dob() { return this.registerForm.get('dob') as FormControl; }
  get sex() { return this.registerForm.get('sex') as FormControl; }
  get email() { return this.registerForm.get('email') as FormControl; }
  get phoneNumber() { return this.registerForm.get('phoneNumber') as FormControl; }
  get password() { return this.registerForm.get('password') as FormControl; }
  get repeatPassword() { return this.registerForm.get('repeatPassword') as FormControl; }

  resetOtpState() {
    this.otpSent = false;
    this.otpVerified = false;
    this.otpCode = '';
    this.otpEmail = '';
  }

  sendOtpToEmail(): void {
    const emailValue = (this.email?.value || '').trim();

    if (!this.email || !this.email.valid || !emailValue) {
      this.toastr.error('Please enter a valid email address.');
      return;
    }

    this.isSendingOtp = true;
    this.otpEmail = emailValue;
    console.log('Sending OTP to:', this.otpEmail);

    this.service.sendEmailOtp(this.otpEmail).subscribe(
      () => {
        this.otpSent = true;
        this.otpVerified = false;
        this.otpCode = '';
        this.toastr.success('Verification code sent to your email.');
        this.isSendingOtp = false;
      },
      () => {
        this.toastr.error('Failed to send verification code.');
        this.isSendingOtp = false;
      }
    );
  }

  verifyOtpCode(): void {
    if (!this.otpCode || this.otpCode.length !== 4) {
      this.toastr.error('Please enter the 4-digit code.');
      return;
    }

    this.isVerifyingOtp = true;

    this.service.verifyEmailOtp(this.otpEmail, this.otpCode).subscribe({
      next: (res) => {
        console.log('Verify OTP response:', res);
        this.otpVerified = true;
        this.toastr.success('Email verified successfully.');
        this.isVerifyingOtp = false;
        this.onSubmit();
      },
      error: (err) => {
        console.error('Verify OTP error:', err);
        console.error('Verify OTP error message:', err?.message);
        console.error('Verify OTP error code:', err?.code);
        this.toastr.error(err?.message || 'Invalid or expired code.');
        this.isVerifyingOtp = false;
      }
    });
  }

  onSubmit() {
    const sexValue = this.selectSex?.nativeElement?.value;
    if (sexValue === '') {
      this.toastr.error('Please choose sex');
      return;
    }

    const sex = parseInt(sexValue, 10);

    if (!this.registerForm.valid || this.isUserExist || this.isCheckingUser) {
      return;
    }

    if (!this.otpVerified) {
      if (!this.otpSent) {
        this.sendOtpToEmail();
        return;
      }

      if (!this.otpCode || this.otpCode.length !== 4) {
        this.toastr.error('Please enter the 4-digit code before continuing.');
        return;
      }

      this.verifyOtpCode();
      return;
    }

    const name = this.name.value;
    const email = this.email.value;
    const phoneNumber = this.phoneNumber.value;
    const password = this.password.value;
    const dob = this.dob.value;
    const emailVerified = true;

    this.showSpinner = true;

    this.service.register(name, email, phoneNumber, password, dob, sex, emailVerified)
      .pipe(catchError(this.handleError))
      .subscribe(
        result => {
          // this.showSpinner = false;
          this.toastr.success('Register Successfully');
          this.loginRegister(email, password);
          // this.zone.run(() => {
          //   this.router.navigate(['login']);
          // });
        },
        error => {
          this.showSpinner = false;
          this.toastr.error(error || 'Register failed');
        }
      );
  }

  loginRegister(email:any, password: any):void {
    this.authService.login(email, password)
      .then(async (user) => {
        const isOtpVerified = await this.authService.isUserVerified(user.uid);

        if (!isOtpVerified) {
          this.toastr.error('Please verify your email using the OTP code first.');
          this.showSpinner = false;
          return;
        }

        this.service.checkUser(user.uid).then(userData => {
          const type = userData?.type;
          localStorage.setItem('user-type', type);

          if (type == 1) {
            this.zone.run(() => {
              this.router.navigate(['my-account/courses']);
            });
          } else {
            this.zone.run(() => {
              this.router.navigate(['my-account/teacher-comments']);
            });
          }
        }).catch(() => {
          this.toastr.error('Oops, Something went wrong!');
          this.showSpinner = false;
        });
      })
      .catch((error:any) => {
        this.toastr.error(error?.message || 'Invalid email or password!');
        this.showSpinner = false;
      });
  }

  handleError(error: HttpErrorResponse) {
    return throwError('Register failed');
  }

  onKeyPressEvent(evt: KeyboardEvent) {
    const charCode = (evt.which) ? evt.which : evt.keyCode;
    if (charCode === 48 && this.phoneNumber.value === '') {
      return false;
    }
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  onEmailTextFieldKeyUp(event: any) {
    const emailValue = event.target.value;

    if (this.otpEmail && this.otpEmail !== emailValue) {
      this.resetOtpState();
    }

    this.subject.next(emailValue);
  }

  changeSex(e: any) {
    this.sex.setValue(e.target.value, { onlySelf: true });
  }
}
